import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, Clock, UserCircle } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClaimRequestsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingId, setRejectingId] = useState(null);
  const [processing, setProcessing] = useState(null);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/claim-requests${filter ? `?status=${filter}` : ''}`, { withCredentials: true });
      setRequests(res.data || []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleApprove = async (claimId) => {
    setProcessing(claimId);
    try {
      const res = await axios.post(`${API}/admin/claim-requests/${claimId}/approve`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (claimId) => {
    setProcessing(claimId);
    try {
      const res = await axios.post(`${API}/admin/claim-requests/${claimId}/reject`,
        { reason: rejectReason || "Request declined by admin" },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      setRejectingId(null);
      setRejectReason("");
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessing(null);
    }
  };

  const pendingCount = requests.filter(r => r.status === "pending").length;

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <UserCircle className="w-5 h-5 text-primary" weight="fill" />
          Profile Claim Requests
          {pendingCount > 0 && filter !== "pending" && (
            <Badge className="bg-destructive/20 text-destructive border-destructive/30 text-xs ml-1">{pendingCount} pending</Badge>
          )}
        </CardTitle>
        <div className="flex gap-1">
          {["pending", "approved", "rejected", ""].map(f => (
            <Button key={f || "all"} size="sm" variant={filter === f ? "default" : "ghost"}
              onClick={() => setFilter(f)} className="text-xs h-7"
              data-testid={`filter-${f || "all"}`}
            >
              {f === "" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary mx-auto" />
          </div>
        ) : requests.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground text-sm">
            No {filter || ""} claim requests
          </p>
        ) : (
          <div className="space-y-3">
            {requests.map(req => (
              <div
                key={req.id}
                className={`p-3 rounded-lg border ${
                  req.status === "pending" ? "border-secondary/30 bg-secondary/5" :
                  req.status === "approved" ? "border-primary/30 bg-primary/5" :
                  "border-destructive/20 bg-destructive/5"
                }`}
                data-testid={`claim-request-${req.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{req.user_name || req.user_email}</span>
                      <span className="text-xs text-muted-foreground">wants to claim</span>
                      <span className="text-sm font-bold text-primary">{req.player_name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                      <span>{req.user_email}</span>
                      <span>{formatDate(req.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {req.status === "pending" ? (
                      <>
                        <Button
                          size="sm" onClick={() => handleApprove(req.id)}
                          disabled={processing === req.id}
                          className="bg-primary text-primary-foreground h-7 text-xs"
                          data-testid={`approve-claim-${req.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5 mr-1" weight="fill" />
                          Approve
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => setRejectingId(rejectingId === req.id ? null : req.id)}
                          disabled={processing === req.id}
                          className="border-destructive/30 text-destructive h-7 text-xs"
                          data-testid={`reject-claim-${req.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" weight="fill" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <Badge variant="outline" className={`text-[10px] capitalize ${
                        req.status === "approved" ? "border-primary/30 text-primary" : "border-destructive/30 text-destructive"
                      }`}>
                        {req.status === "approved" ? <CheckCircle className="w-3 h-3 mr-0.5" weight="fill" /> : <XCircle className="w-3 h-3 mr-0.5" weight="fill" />}
                        {req.status}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Reject reason input */}
                {rejectingId === req.id && (
                  <div className="mt-2 flex gap-2" data-testid={`reject-form-${req.id}`}>
                    <Input
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection (optional)"
                      className="bg-background border-border text-sm h-8"
                    />
                    <Button size="sm" className="bg-destructive text-destructive-foreground h-8 text-xs"
                      onClick={() => handleReject(req.id)} disabled={processing === req.id}
                    >
                      Confirm Reject
                    </Button>
                  </div>
                )}

                {/* Show reason for rejected */}
                {req.status === "rejected" && req.reason && (
                  <p className="text-[10px] text-destructive mt-1">Reason: {req.reason}</p>
                )}
                {req.resolved_at && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">Resolved: {formatDate(req.resolved_at)}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
